import { CanActivateFn, NavigationEnd, Router } from '@angular/router';
import { inject } from '@angular/core';
import { xAuthToken } from '@core/services/auth/auth.service';
import { AUTH_SERVICE } from '@core/services/auth/auth.token';
import { take } from 'rxjs';
import { defaultIfEmpty, filter, map, tap } from 'rxjs/operators';
import { AuthPath } from '../../../app-routing.constants';

/**
 * Strips this guard's own control params (logout/login/token/returnTo) out of a router state
 * URL before using it as a returnTo fallback — otherwise a control param round-trips into the
 * next navigation (e.g. `?logout=true` surviving into the post-login redirect target) instead
 * of being consumed once, here.
 */
function stripGuardControlParams(url: string): string {
  const [path, query] = url.split('?', 2);
  if (!query) return path;
  const params = new URLSearchParams(query);
  ['logout', 'login', 'token', 'returnTo'].forEach((key) => params.delete(key));
  const remaining = params.toString();
  return remaining ? `${path}?${remaining}` : path;
}

/**
 * Authentication guard that controls route access based on user authentication status.
 * Shared across nymbl2 and nymbl3 — the redirect destination when unauthenticated
 * comes from the injected AUTH_SERVICE (see AuthApi#redirectToLogin), so this guard
 * stays app-agnostic. The token/login/logout query-param handling below is legacy V1
 * behavior; it's a no-op for apps whose AuthApi implementation never produces those
 * query params (e.g. nymbl3's cookie-based SSO flow).
 *
 * Key features:
 * - Handles token-based authentication from URL parameters
 * - Manages login/logout redirects with return paths
 * - Preserves requested URLs for post-authentication navigation
 *
 * @param route Current route being activated
 * @param state Current router state
 * @returns true if access is allowed, or a UrlTree for redirect
 */
export const authGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AUTH_SERVICE);
  const authPath = `/${AuthPath}`;
  const returnTo =
    route.queryParams['returnTo'] || stripGuardControlParams(state.url);

  console.debug('[authGuard] evaluating', state.url, {
    queryParams: route.queryParams,
    returnTo
  });

  // if there's a token in the path, try to authenticate
  const token = route.queryParams['token'];
  if (!!token) {
    console.debug('[authGuard] token query param present, authenticating', {
      token
    });
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        tap(() => {
          console.debug('[authGuard] NavigationEnd, stripping token param');
          router.navigate([], {
            queryParams: {
              token: null
            },
            queryParamsHandling: 'merge'
          });
        }),
        take(1)
      )
      .subscribe();
    const authenticated = !!authService.authenticateFromToken(token);
    console.debug('[authGuard] authenticateFromToken result', authenticated);
    if (authenticated) {
      console.debug('[authGuard] token valid, allowing access');
      return true;
    } else {
      // log out if the token is 'undefined'
      console.debug('[authGuard] token invalid, logging out');
      authService.logout().subscribe();
    }
  }

  // check query params to determine if we should log out
  const logout = route.queryParams['logout'] === 'true';
  if (logout) {
    console.debug('[authGuard] logout query param present, logging out');
    // Redirect only after the logout request settles — a same-tick redirect aborts it, so the
    // session survives server-side and a hard refresh silently signs the user back in.
    // defaultIfEmpty keeps the redirect reliable even if logout() completes without emitting.
    return authService.logout().pipe(
      defaultIfEmpty(undefined),
      map(() => {
        console.debug('[authGuard] redirecting to login after logout', {
          returnTo
        });
        authService.redirectToLogin(returnTo);
        return false;
      })
    );
  }

  // check query params if we are logging in.
  const login = route.queryParams['login'] === 'true';
  if (login) {
    console.debug(
      '[authGuard] login query param present, checking stored token'
    );
    // fetch the token from localstorage.
    const token = localStorage.getItem(xAuthToken);
    // validate the token
    const authenticated = !!authService.authenticateFromToken(token);
    console.debug(
      '[authGuard] stored token authenticateFromToken result',
      authenticated
    );
    // if valid route the user to their return path to remove the parameter from the URL.
    if (authenticated) {
      console.debug('[authGuard] stored token valid, routing to returnTo', {
        returnTo
      });
      return router.createUrlTree([returnTo || '']);
    } else {
      console.debug('[authGuard] stored token invalid, logging out');
      authService.logout().subscribe();
    }
  }

  console.debug(
    '[authGuard] falling through to isLoggedIn$ check for',
    state.url
  );
  return authService.isLoggedIn$.pipe(
    map((loggedIn) => {
      console.debug(
        '[authGuard] isLoggedIn$ emitted',
        loggedIn,
        'for',
        state.url
      );
      if (!loggedIn) {
        console.debug('[authGuard] not logged in, redirecting to login', {
          returnTo
        });
        authService.redirectToLogin(returnTo);
      }
      return loggedIn;
    })
  );
};
